import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

const ROLE_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "super_admin", label: "Super Admin" },
];

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export default function Admin() {
  const [session, setSession] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [signInEmail, setSignInEmail] = useState("");
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newAdminRole, setNewAdminRole] = useState("admin");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadAdminUsers = useCallback(async (email) => {
    setLoading(true);
    const { data, error } = await supabase
      .from("admin_users")
      .select("email,role,created_at")
      .order("created_at", { ascending: true });

    if (error) {
      setMessage(
        `Unable to load administrator list. ${
          error.message || "Please try again."
        }`,
      );
      setAdminUsers([]);
    } else {
      setAdminUsers(data || []);
      setMessage("");
    }

    setLoading(false);
  }, []);

  const handleAuthState = useCallback(
    async (currentSession) => {
      setSession(currentSession || null);
      setMessage("");

      if (!currentSession?.user?.email) {
        setUserEmail("");
        setAdminUsers([]);
        setLoading(false);
        return;
      }

      const email = currentSession.user.email.toLowerCase();
      setUserEmail(email);
      await loadAdminUsers(email);
    },
    [loadAdminUsers],
  );

  useEffect(() => {
    async function init() {
      const { data } = await supabase.auth.getSession();
      await handleAuthState(data?.session);
    }

    init();

    const { data: listener } = supabase.auth.onAuthStateChange(
      async (_event, currentSession) => {
        await handleAuthState(currentSession);
      },
    );

    return () => listener.subscription.unsubscribe();
  }, [handleAuthState]);

  const effectiveUserEmail = useMemo(() => {
    return session?.user?.email?.toLowerCase() || "";
  }, [session]);

  const isAdmin = useMemo(() => {
    return adminUsers.some(
      (admin) => admin.email.toLowerCase() === effectiveUserEmail,
    );
  }, [adminUsers, effectiveUserEmail]);

  const isSuperAdmin = useMemo(() => {
    return adminUsers.some(
      (admin) =>
        admin.email.toLowerCase() === effectiveUserEmail &&
        admin.role === "super_admin",
    );
  }, [adminUsers, effectiveUserEmail]);

  async function handleSignIn(event) {
    event.preventDefault();

    if (!isValidEmail(signInEmail)) {
      setMessage("Enter a valid email address.");
      return;
    }

    setIsSubmitting(true);
    const { error } = await supabase.auth.signInWithOtp({
      email: signInEmail.trim().toLowerCase(),
    });
    setIsSubmitting(false);

    if (error) {
      setMessage(`Unable to send sign-in link. ${error.message}`);
      return;
    }

    setMessage("Magic link sent. Check your email to complete sign-in.");
    setSignInEmail("");
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    setSession(null);
    setAdminUsers([]);
    setUserEmail("");
    setMessage("Signed out.");
  }

  async function handleCreateAdmin(event) {
    event.preventDefault();

    if (!isValidEmail(newAdminEmail)) {
      setMessage("Enter a valid email address for the new admin.");
      return;
    }

    setIsSubmitting(true);
    const { error } = await supabase.from("admin_users").insert([
      {
        email: newAdminEmail.trim().toLowerCase(),
        role: newAdminRole,
      },
    ]);
    setIsSubmitting(false);

    if (error) {
      setMessage(`Unable to create admin user. ${error.message}`);
      return;
    }

    setMessage(`Admin user ${newAdminEmail} was created successfully.`);
    setNewAdminEmail("");
    await loadAdminUsers(effectiveUserEmail);
  }

  return (
    <div className="adminPage">
      <div className="container">
        <div className="successCard adminCard">
          <div className="successCard__top">
            <Link className="backLink" to="/">
              ← Back to homepage
            </Link>
            {session?.user?.email ? (
              <button className="btn btn--secondary" onClick={handleSignOut}>
                Sign out
              </button>
            ) : null}
          </div>

          <h1 className="successTitle">Admin dashboard</h1>

          <div className="successBody">
            <p>
              This page is protected and only available to approved SmartUp
              administrators.
            </p>

            {message ? <div className="adminMessage">{message}</div> : null}

            {!session ? (
              <form onSubmit={handleSignIn} className="adminForm">
                <label htmlFor="signInEmail">Admin email</label>
                <input
                  id="signInEmail"
                  type="email"
                  value={signInEmail}
                  placeholder="you@domain.com"
                  onChange={(event) => setSignInEmail(event.target.value)}
                />
                <button
                  className="btn btn--primary"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending link…" : "Send magic link"}
                </button>
              </form>
            ) : !isAdmin ? (
              <div>
                <p>
                  You are signed in as <strong>{userEmail}</strong>, but this
                  address is not registered as an administrator.
                </p>
                <p>
                  If you believe this is an error, sign out and verify the
                  account.
                </p>
              </div>
            ) : (
              <div className="adminContent">
                <div className="adminIntro">
                  <p>
                    Signed in as <strong>{userEmail}</strong>.
                  </p>
                  <p>
                    Role: <strong>{isSuperAdmin ? "Super Admin" : "Admin"}</strong>
                  </p>
                </div>

                <div className="adminSection">
                  <h2>Current admins</h2>
                  {loading ? (
                    <p>Loading admin users…</p>
                  ) : (
                    <table className="adminTable">
                      <thead>
                        <tr>
                          <th>Email</th>
                          <th>Role</th>
                          <th>Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminUsers.map((admin) => (
                          <tr key={admin.email}>
                            <td>{admin.email}</td>
                            <td>{admin.role.replace("_", " ")}</td>
                            <td>
                              {admin.created_at
                                ? new Date(admin.created_at).toLocaleString()
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {isSuperAdmin ? (
                  <div className="adminSection">
                    <h2>Create admin users</h2>
                    <form onSubmit={handleCreateAdmin} className="adminForm">
                      <label htmlFor="newAdminEmail">New admin email</label>
                      <input
                        id="newAdminEmail"
                        type="email"
                        value={newAdminEmail}
                        placeholder="new-admin@domain.com"
                        onChange={(event) => setNewAdminEmail(event.target.value)}
                      />

                      <label htmlFor="newAdminRole">Role</label>
                      <select
                        id="newAdminRole"
                        value={newAdminRole}
                        onChange={(event) => setNewAdminRole(event.target.value)}
                      >
                        {ROLE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>

                      <button
                        className="btn btn--primary"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Creating…" : "Create admin user"}
                      </button>
                    </form>
                  </div>
                ) : (
                  <div className="adminSection">
                    <p>
                      Only super administrators can create new admin users.
                      Contact your organization owner if you need access.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
